import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../lib/api';
import api from '../lib/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewingCompany, setViewingCompany] = useState(null);
  const [companyConfig, setCompanyConfig] = useState(null);
  const [configLoading, setConfigLoading] = useState(false);

  // Fetch company configuration (modules, branding, custom fields)
  const fetchCompanyConfig = async (companyId) => {
    if (!companyId) return;
    setConfigLoading(true);
    try {
      // Try platform endpoint first (for super admins viewing a company)
      const response = await api.get(`/platform/companies/${companyId}/config`);
      setCompanyConfig(response.data);
      // Apply branding
      applyBranding(response.data.branding);
    } catch (error) {
      // Fallback: try company-config endpoint (for regular users)
      try {
        const configResponse = await api.get('/company-config');
        if (configResponse.data) {
          setCompanyConfig(configResponse.data);
          applyBranding(configResponse.data.branding);
        } else {
          // Use defaults
          setCompanyConfig({
            modules_config: getDefaultModules(),
            custom_fields: {},
            branding: { primary_color: '#f97316' }
          });
        }
      } catch (e) {
        // Use defaults
        setCompanyConfig({
          modules_config: getDefaultModules(),
          custom_fields: {},
          branding: { primary_color: '#f97316' }
        });
      }
    } finally {
      setConfigLoading(false);
    }
  };

  // Default modules configuration
  const getDefaultModules = () => ({
    reinigingen: { enabled: true, display_name: 'Reinigingen' },
    installaties: { enabled: true, display_name: 'Installaties' },
    stock: { enabled: true, display_name: 'Voorraad' },
    agenda: { enabled: true, display_name: 'Agenda' },
    facturen: { enabled: true, display_name: 'Facturen' },
    offertes: { enabled: true, display_name: 'Offertes' }
  });

  // Apply branding colors to CSS variables
  const applyBranding = (branding) => {
    if (!branding) return;
    
    const root = document.documentElement;
    
    if (branding.primary_color) {
      // Convert hex to HSL for Tailwind CSS variable
      const hex = branding.primary_color;
      const hsl = hexToHSL(hex);
      if (hsl) {
        root.style.setProperty('--primary', `${hsl.h} ${hsl.s}% ${hsl.l}%`);
        root.style.setProperty('--primary-foreground', hsl.l > 50 ? '0 0% 0%' : '0 0% 100%');
      }
    }
  };

  // Helper: Convert hex color to HSL
  const hexToHSL = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (!result) return null;
    
    let r = parseInt(result[1], 16) / 255;
    let g = parseInt(result[2], 16) / 255;
    let b = parseInt(result[3], 16) / 255;
    
    const max = Math.max(r, g, b), min = Math.min(r, g, b);
    let h, s, l = (max + min) / 2;
    
    if (max === min) {
      h = s = 0;
    } else {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
        case g: h = ((b - r) / d + 2) / 6; break;
        case b: h = ((r - g) / d + 4) / 6; break;
        default: h = 0;
      }
    }
    
    return { h: Math.round(h * 360), s: Math.round(s * 100), l: Math.round(l * 100) };
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    const savedViewingCompany = localStorage.getItem('viewingCompany');
    
    if (token && savedUser) {
      const parsedUser = JSON.parse(savedUser);
      setUser(parsedUser);
      
      // Restore viewing company if super admin
      if (savedViewingCompany && parsedUser.platform_role === 'super_admin') {
        const company = JSON.parse(savedViewingCompany);
        setViewingCompany(company);
        fetchCompanyConfig(company.id);
      } else if (parsedUser.company_id) {
        // Regular user: fetch their company config
        fetchCompanyConfig(parsedUser.company_id);
      }
      
      // Verify token is still valid — enkel uitloggen bij een echte auth-fout,
      // niet bij tijdelijke netwerk/redeploy-fouten (anders wordt men bij elke hiccup uitgelogd)
      authAPI.getMe()
        .then((res) => {
          setUser(res.data);
          localStorage.setItem('user', JSON.stringify(res.data));
        })
        .catch((err) => {
          const st = err.response?.status;
          if (st === 401 || st === 403) logout();
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email, password) => {
    const response = await authAPI.login({ email, password });
    const { token, user: userData } = response.data;
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    // Clear any viewing company on fresh login
    setViewingCompany(null);
    localStorage.removeItem('viewingCompany');
    // Fetch company config for regular users
    if (userData.company_id) {
      fetchCompanyConfig(userData.company_id);
    }
    return userData;
  };

  const register = async (data) => {
    const response = await authAPI.register(data);
    const { token, user: userData } = response.data;
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('viewingCompany');
    setUser(null);
    setViewingCompany(null);
    setCompanyConfig(null);
    // Reset branding to defaults
    document.documentElement.style.removeProperty('--primary');
    document.documentElement.style.removeProperty('--primary-foreground');
  };

  const hasRole = (roles) => {
    if (!user) return false;
    const userRole = user.role?.toLowerCase();
    
    // Treat 'verkoper' as 'sales' alias
    const normalizedUserRole = userRole === 'verkoper' ? 'sales' : userRole;
    
    if (typeof roles === 'string') {
      const normalizedRole = roles.toLowerCase() === 'verkoper' ? 'sales' : roles.toLowerCase();
      return normalizedUserRole === normalizedRole;
    }
    
    // Normalize all roles in the array
    const normalizedRoles = roles.map(r => r.toLowerCase() === 'verkoper' ? 'sales' : r.toLowerCase());
    return normalizedRoles.includes(normalizedUserRole);
  };

  // Check if a module is enabled
  const isModuleEnabled = (moduleKey) => {
    if (!companyConfig?.modules_config) return true; // Default: all enabled
    return companyConfig.modules_config[moduleKey]?.enabled !== false;
  };

  // Get module display name
  const getModuleName = (moduleKey, defaultName) => {
    if (!companyConfig?.modules_config?.[moduleKey]) return defaultName;
    return companyConfig.modules_config[moduleKey].display_name || defaultName;
  };

  // Get custom fields for an entity type
  const getCustomFields = (entityType) => {
    return companyConfig?.custom_fields?.[entityType] || [];
  };

  // Super Admin: Enter a company's CRM view
  const enterCompanyCRM = (company) => {
    if (user?.platform_role !== 'super_admin') return;
    setViewingCompany(company);
    localStorage.setItem('viewingCompany', JSON.stringify(company));
    fetchCompanyConfig(company.id);
  };

  // Super Admin: Exit company CRM view, return to platform
  const exitCompanyCRM = () => {
    setViewingCompany(null);
    setCompanyConfig(null);
    localStorage.removeItem('viewingCompany');
    // Reset branding
    document.documentElement.style.removeProperty('--primary');
    document.documentElement.style.removeProperty('--primary-foreground');
  };

  // Enhanced user object with viewing context
  const enhancedUser = user ? {
    ...user,
    viewingCompanyId: viewingCompany?.id || null,
    companyName: viewingCompany?.name || null,
    isViewingCompany: !!viewingCompany,
  } : null;

  const value = {
    user: enhancedUser,
    loading,
    login,
    register,
    logout,
    hasRole,
    isAuthenticated: !!user,
    // Super Admin functions
    viewingCompany,
    enterCompanyCRM,
    exitCompanyCRM,
    isSuperAdmin: user?.platform_role === 'super_admin',
    // Company configuration
    companyConfig,
    configLoading,
    isModuleEnabled,
    getModuleName,
    getCustomFields,
    refreshCompanyConfig: () => fetchCompanyConfig(viewingCompany?.id || user?.company_id),
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
